// Dynamic covers: a card whose `coverType` is 'dynamic' has a cover page (cardPages[0]) designed like an
// inner page. The backend renders it per recipient at GET /cover/:cardId.jpg?<tag>=<value>…, which is
// what emails and external tools embed. Static covers (the default) stay a single image.

const TAG_PATTERN = /{{\s*([^{}]+?)\s*}}/g;

export const isDynamicCover = (card?: { coverType?: string } | null): boolean => card?.coverType === 'dynamic';

/** Tags a cover page uses: `{{tag}}` in its text boxes and `mergeTag` on its images. Unique, first-seen order. */
export const coverUsedTags = (page: any): string[] => {
	const tags = new Set<string>();
	(page?.textBoxes || []).forEach((box: any) => {
		if (typeof box?.text !== 'string') return;
		Array.from(box.text.matchAll(TAG_PATTERN) as IterableIterator<RegExpMatchArray>).forEach((m) => tags.add(m[1]));
	});
	(page?.images || []).forEach((img: any) => img?.mergeTag && tags.add(img.mergeTag));
	return Array.from(tags);
};

const apiBase = () => process.env.REACT_APP_API_URL || '';

/** The rendered cover for concrete values (empty values use the card's fallbacks server-side). */
export const coverImageUrl = (cardId: string, params: Record<string, string | undefined> = {}): string => {
	const query = Object.keys(params)
		.filter((key) => params[key] !== undefined && params[key] !== '')
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key] as string)}`)
		.join('&');
	const base = `${apiBase()}/cover/${cardId}.jpg`;
	return query ? `${base}?${query}` : base;
};

/**
 * Cover URL for an external email tool: each tag's value is that tool's merge syntax, left unencoded so
 * the tool can substitute it before the email goes out (same convention as the personalised /viewcard link).
 */
export const coverEmbedSrc = (cardId: string, tags: string[], formatTag: (tag: string) => string): string => {
	const query = tags.map((tag) => `${encodeURIComponent(tag)}=${formatTag(tag)}`).join('&');
	const base = `${apiBase()}/cover/${cardId}.jpg`;
	return query ? `${base}?${query}` : base;
};

/** Id of the cover's own image on a dynamic cover. */
export const COVER_IMAGE_ID = 'images_cover';

/**
 * On a dynamic cover the cover image is an ordinary editor image (selectable, movable, resizable, deletable)
 * rather than the locked full-bleed image of a static cover. Unlocks it if the page still has the static form.
 */
export const withEditableCover = (pages: any[]): any[] => {
	const first = pages?.[0]?.images?.[0];
	if (!first || first.id || first.draggable !== false) return pages;
	const cover = { ...pages[0], images: [{ ...first, id: COVER_IMAGE_ID, name: 'object', draggable: true }, ...pages[0].images.slice(1)] };
	return [cover, ...pages.slice(1)];
};

export const hasCoverImage = (page: any): boolean => !!page?.images?.some((img: any) => img?.id === COVER_IMAGE_ID);
